/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  useAllMatchStatsWC26, useMatchStatsWC26, useWC26ScoringRules, useWC26UserTeams,
} from '../../queries';
import { Fixture, Scores, UserTeams } from '../../types';
import { calculateResults, calculateScores, getMostRedsWinnerIds } from '../../utils/scoringUtils';
import NameLink from '../NameLink/NameLink';
import { MatchTooltip } from '../ScoreboardWC26/ScoreTooltip';
import './FixturesOfTheDayWC26.css';

interface FixtureCardWC26Props {
  fixture: Fixture;
  teamAllocs: UserTeams[];
}

function ScoreBadge({ score }: { score: Scores }) {
  const total = score.score + score.groupWinner + score.goldenBoot
    + score.earliestGoal + score.latestGoal + score.hattrick
    + score.directFk + score.olimpico + score.keeperGoal
    + score.mostReds + score.teamGoals3plus;

  const colourClass = total < 0 ? 'negative' : total > 0 ? 'positive' : 'neutral';
  const label = total >= 0 ? `+${total}` : `${total}`;

  return (
    <div className={`fixture-wc26-score ${colourClass}`}>
      <MatchTooltip score={score} direction="up" label={label} />
    </div>
  );
}

const ZERO_SCORE: Scores = {
  name: '',
  score: 0,
  winsScore: 0,
  drawsScore: 0,
  cleanSheetsScore: 0,
  redCardsScore: 0,
  ownGoalsScore: 0,
  goldenBoot: 0,
  groupWinner: 0,
  earliestGoal: 0,
  latestGoal: 0,
  hattrick: 0,
  directFk: 0,
  olimpico: 0,
  keeperGoal: 0,
  mostReds: 0,
  teamGoals3plus: 0,
};

const LIVE_STATUSES = ['1H', '2H', 'HT', 'ET', 'BT', 'LIVE', 'P'];
const FINISHED_STATUSES = ['FT', 'AET', 'PEN'];

const ZERO_TEAM_RESULTS = {
  wins: 0,
  draws: 0,
  redCards: 0,
  cleanSheets: 0,
  ownGoals: 0,
  teamGoals3plus: 0,
};

const DUMMY_TEAM_ID = '__none__';

function FixtureCardWC26({ fixture, teamAllocs }: FixtureCardWC26Props) {
  const [matchStatsLoading, matchStats] = useMatchStatsWC26(fixture.id.toString(), 60000);
  const [scoringRulesLoading, scoringRules] = useWC26ScoringRules();
  const [userTeamsLoading, userTeams] = useWC26UserTeams();
  const [, allMatchStats] = useAllMatchStatsWC26();
  const [scores, setScores] = useState<Record<string, Scores>>({});

  const isLoading = matchStatsLoading || scoringRulesLoading || userTeamsLoading;

  useEffect(() => {
    if (isLoading || !userTeams || !matchStats || !scoringRules) return;
    const allTeamResults = calculateResults(userTeams, allMatchStats ?? []);
    const mostRedsWinnerIds = getMostRedsWinnerIds(allTeamResults, userTeams);
    const teamResults = calculateResults(userTeams, [matchStats]);
    const teamResultsWithDummy = { ...teamResults, [DUMMY_TEAM_ID]: { ...ZERO_TEAM_RESULTS } };

    const scoresByTeamId: Record<string, Scores> = {};
    userTeams.forEach((user) => {
      [user.team1Id, user.team2Id].forEach((teamId) => {
        if (!(teamId in teamResults)) return;
        const isT1 = teamId === user.team1Id;
        const soloUser = {
          name: user.name,
          team1: '',
          team2: '',
          team1Id: isT1 ? teamId : DUMMY_TEAM_ID,
          team2Id: isT1 ? DUMMY_TEAM_ID : teamId,
        };
        const [soloScore] = calculateScores(
          teamResultsWithDummy,
          [soloUser],
          scoringRules,
          undefined,
          undefined,
          undefined,
          undefined,
          [matchStats],
          undefined,
          undefined,
          undefined,
          mostRedsWinnerIds
        );
        scoresByTeamId[teamId] = soloScore;
      });
    });
    setScores(scoresByTeamId);
  }, [isLoading, userTeams, matchStats, scoringRules, allMatchStats]);

  const findNamesByTeam = (teamId: string) => teamAllocs
    .filter((a) => a.team1Id === teamId.toString() || a.team2Id === teamId.toString())
    .map((a) => a.name);

  const isLive = matchStats?.status ? LIVE_STATUSES.includes(matchStats.status.short) : false;
  const isFinished = matchStats?.status
    ? FINISHED_STATUSES.includes(matchStats.status.short)
    : false;
  const elapsed = matchStats?.status?.elapsed;
  const statusShort = matchStats?.status?.short;

  const homeNames = findNamesByTeam(fixture.homeId);
  const awayNames = findNamesByTeam(fixture.awayId);
  const homeScoreObj = scores[fixture.homeId.toString()] ?? ZERO_SCORE;
  const awayScoreObj = scores[fixture.awayId.toString()] ?? ZERO_SCORE;

  return (
    <div className={`fixture-wc26-card ${isLive ? 'live' : ''}`}>
      <div className="fixture-wc26-team">
        <img src={fixture.homeLogo} alt={`${fixture.home} logo`} />
        <span>{fixture.home}</span>
        <div className="fixture-wc26-team-names">
          {homeNames.map((name, i) => (
            <React.Fragment key={name}>
              {i > 0 && ', '}
              <NameLink name={name} />
            </React.Fragment>
          ))}
        </div>
        <ScoreBadge score={homeScoreObj} />
      </div>
      <div className="fixture-wc26-middle">
        {isLive ? (
          <div className="fixture-wc26-live">
            <span className="fixture-wc26-live-dot" />
            <span className="fixture-wc26-live-elapsed">
              {statusShort === 'HT' ? 'HT' : elapsed ? `${elapsed}'` : 'LIVE'}
            </span>
          </div>
        ) : isFinished ? (
          <div className="fixture-wc26-ft">{statusShort}</div>
        ) : (
          <div className="fixture-wc26-time">
            {new Date(fixture.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        <div className="fixture-wc26-score-line">
          <span>{matchStats?.score.home ?? '-'}</span>
          <span className="fixture-wc26-vs">{statusShort && statusShort !== 'NS' ? '—' : 'vs'}</span>
          <span>{matchStats?.score.away ?? '-'}</span>
        </div>
      </div>
      <div className="fixture-wc26-team">
        <img src={fixture.awayLogo} alt={`${fixture.away} logo`} />
        <span>{fixture.away}</span>
        <div className="fixture-wc26-team-names">
          {awayNames.map((name, i) => (
            <React.Fragment key={name}>
              {i > 0 && ', '}
              <NameLink name={name} />
            </React.Fragment>
          ))}
        </div>
        <ScoreBadge score={awayScoreObj} />
      </div>
    </div>
  );
}

export default FixtureCardWC26;
